/**
 * Unified Chat Page — session sidebar + AgentChatPage engine
 * All chat functionality (voice, tools, streaming, attachments) comes from AgentChatPage.
 * This page adds: session list, search, agent switching.
 */

import { useState, useEffect } from 'react';
import {
  Plus,
  Search,
  Loader2,
  MessageSquare,
  Phone,
  MessageCircle,
} from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { coworkApi } from '../api/coworkApi';
import { avatarSrc } from '../avatarSrc';
import { AgentChatPage } from './AgentChatPage';
import { AgentAvatar } from './AgentAvatar';
import { CallInvitePanel } from './CallInvitePanel';
import type { Agent } from '../types/cowork';

// ─── Types ────────────────────────────────────────────────────────

interface SavedSession {
  id: string;
  agentId: string;
  agentName: string;
  agentEmoji: string;
  agentAvatarUrl?: string;
  agentColor: string;
  title: string | null;
  startedAt: string;
  lastMessageAt: string;
  messageCount: number;
}

// ─── Helpers ──────────────────────────────────────────────────────

function agentAvatar(session: SavedSession, size = 'w-8 h-8', textSize = 'text-sm') {
  return (
    <AgentAvatar
      name={session.agentName}
      avatarUrl={session.agentAvatarUrl}
      emoji={session.agentEmoji}
      accentColor={session.agentColor}
      className={`${size} rounded-xl shrink-0`}
      textClassName={textSize}
    />
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString();
}

function dateGroup(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'This week';
  if (diffDays < 30) return 'This month';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// ─── Main Component ───────────────────────────────────────────────

export function ChatPage() {
  const [sessions, setSessions] = useState<SavedSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSession, setSelectedSession] = useState<SavedSession | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [showGroupChatPicker, setShowGroupChatPicker] = useState(false);
  const [showGroupCallPicker, setShowGroupCallPicker] = useState(false);
  const [callAgents, setCallAgents] = useState<Agent[]>([]);
  // Distinguishes a live voice call from a multi-agent text chat (both use callAgents).
  const [isVoiceCall, setIsVoiceCall] = useState(false);

  // Load sessions + agents
  useEffect(() => {
    const load = async () => {
      try {
        const [sessionsData, agentsData] = await Promise.all([
          coworkApi.chats.list(100),
          coworkApi.agents.list(),
        ]);
        setSessions(sessionsData.sessions.map((s: any) => ({
          id: s.id,
          agentId: s.agentId,
          agentName: s.agentName,
          agentEmoji: s.agentEmoji,
          agentAvatarUrl: s.agentAvatarUrl,
          agentColor: s.agentColor,
          title: s.title,
          startedAt: s.startedAt,
          lastMessageAt: s.lastMessageAt,
          messageCount: s.messageCount,
        })));
        setAgents(agentsData);
      } catch (err) {
        console.error('[chat] Failed to load sessions:', err);
      } finally {
        setLoadingSessions(false);
      }
    };
    load();
  }, []);

  const handleSelectSession = (session: SavedSession) => {
    setSelectedSession(session);
    const agent = agents.find(a => a.id === session.agentId);
    if (agent) setSelectedAgent(agent);
  };

  const handleNewChat = () => {
    setSelectedSession(null);
    setSelectedAgent(null);
    setCallAgents([]);
  };

  const handleStartGroupChat = (agentIds: string[]) => {
    setShowGroupChatPicker(false);
    const selected = agents.filter(a => agentIds.includes(a.id));
    setIsVoiceCall(false);
    if (selected.length === 1) {
      setSelectedAgent(selected[0]);
      setCallAgents([]);
    } else if (selected.length > 1) {
      setSelectedAgent(selected[0]);
      setCallAgents(selected);
    }
  };

  const handleStartGroupCall = (agentIds: string[]) => {
    setShowGroupCallPicker(false);
    const selected = agents.filter(a => agentIds.includes(a.id));
    setIsVoiceCall(true);
    if (selected.length >= 1) {
      setSelectedAgent(selected[0]);
      setCallAgents(selected);
    }
  };

  const handleQuickAgentChat = (agent: Agent) => {
    setSelectedSession(null);
    setIsVoiceCall(false);
    setSelectedAgent(agent);
    setCallAgents([]);
  };

  // Filter + group sessions
  const filteredSessions = searchQuery
    ? sessions.filter(s =>
        (s.title ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.agentName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sessions;

  // Group by agent (most recent per agent) + voice calls as separate entries
  const agentChannels: SavedSession[] = [];
  const seenAgents = new Set<string>();
  filteredSessions.forEach(s => {
    if (s.title?.startsWith('Voice Call')) {
      agentChannels.push(s);
      return;
    }
    if (!seenAgents.has(s.agentId)) {
      seenAgents.add(s.agentId);
      agentChannels.push(s);
    }
  });

  const grouped = new Map<string, SavedSession[]>();
  agentChannels.forEach(s => {
    const group = dateGroup(s.lastMessageAt);
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group)!.push(s);
  });

  return (
    <div className="flex h-[calc(100vh-3.5rem)] bg-[#fafafa]">
      {/* ────────────── Sidebar ────────────── */}
      <div className="w-[280px] shrink-0 bg-white border-r-0 flex flex-col">
        {/* New chat + Group actions */}
        <div className="p-3 pb-2 space-y-1.5">
          <button
            onClick={handleNewChat}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-[13px] text-[#101828] hover:bg-[#f8f5ff] transition-colors border border-gray-100"
            style={{ fontWeight: 600 }}
          >
            <Plus className="w-4 h-4 text-[#be43ff]" />
            New chat
          </button>
          <div className="flex gap-1.5">
            <button
              onClick={() => setShowGroupChatPicker(true)}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] border border-gray-200 text-[#475569] hover:bg-[#f8fafc] hover:text-[#101828] transition-all"
              style={{ fontWeight: 600 }}
            >
              <MessageCircle className="w-3 h-3" />
              Group chat
            </button>
            <button
              onClick={() => setShowGroupCallPicker(true)}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] border border-gray-200 text-[#475569] hover:bg-[#f8fafc] hover:text-[#101828] transition-all"
              style={{ fontWeight: 600 }}
            >
              <Phone className="w-3 h-3" />
              Group call
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 pb-2">
          <div className="flex items-center gap-2 bg-[#f9fafb] rounded-lg px-3 py-2 border border-gray-100">
            <Search className="w-3.5 h-3.5 text-[#98a2b3]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="bg-transparent text-[12px] outline-none flex-1 placeholder:text-[#98a2b3]"
            />
          </div>
        </div>

        {/* Session list */}
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          {loadingSessions ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-[#be43ff] animate-spin" />
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <MessageSquare className="w-8 h-8 text-[#d0d5dd] mb-3" />
              <p className="text-[12px] text-[#98a2b3] text-center">
                {searchQuery ? 'No matching conversations' : 'No conversations yet. Chat with an agent in Cowork to get started.'}
              </p>
            </div>
          ) : (
            Array.from(grouped.entries()).map(([group, groupSessions]) => (
              <div key={group}>
                <div className="px-2 pt-3 pb-1">
                  <span className="text-[11px] text-[#98a2b3] uppercase tracking-wider" style={{ fontWeight: 600 }}>
                    {group}
                  </span>
                </div>
                {groupSessions.map(session => (
                  <button
                    key={session.id}
                    onClick={() => handleSelectSession(session)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-xl transition-all duration-150 mb-0.5 flex items-start gap-2.5',
                      selectedSession?.id === session.id
                        ? 'bg-[#f8f5ff] border border-[#be43ff]/15'
                        : 'hover:bg-[#f9fafb] border border-transparent'
                    )}
                  >
                    <div className="shrink-0 mt-0.5">
                      {agentAvatar(session, 'w-8 h-8', 'text-sm')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <span
                          className={cn(
                            'text-[13px] truncate',
                            selectedSession?.id === session.id ? 'text-[#be43ff]' : 'text-[#101828]'
                          )}
                          style={{ fontWeight: 600 }}
                        >
                          {session.title?.startsWith('Voice Call') ? session.title : session.agentName}
                        </span>
                        <span className="text-[10px] text-[#c4c9d4] shrink-0">{timeAgo(session.lastMessageAt)}</span>
                      </div>
                      <p className="text-[11px] text-[#98a2b3] truncate mt-0.5">
                        {session.title?.startsWith('Voice Call') ? session.agentName : (session.title || 'Start a conversation')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ────────────── Main Chat Area ────────────── */}
      <div className="flex-1 flex flex-col min-w-0 relative bg-[#fafafa] rounded-tl-[24px] overflow-hidden">
        {/* Concave corner */}
        <div className="absolute top-0 left-0 z-10 pointer-events-none" style={{ width: 24, height: 24 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M24 0H0V24C0 10.745 10.745 0 24 0Z" fill="white" />
          </svg>
        </div>

        {selectedAgent ? (
          <AgentChatPage
            key={selectedAgent.id + '-' + (selectedSession?.id ?? 'latest')}
            agent={selectedAgent}
            sessionId={selectedSession?.id}
            onBack={handleNewChat}
            onNavigateToTasks={() => {}}
            callAgents={callAgents.length > 1 ? callAgents : undefined}
            autoVoice={isVoiceCall}
            voiceCall={isVoiceCall}
          />
        ) : (
          /* Empty state — quick-pick agent grid */
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="max-w-[560px] w-full">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#be43ff]/10 to-[#be43ff]/5 flex items-center justify-center mb-5 shadow-sm border border-[#be43ff]/10 mx-auto">
                  <MessageSquare className="w-6 h-6 text-[#be43ff]" />
                </div>
                <h2 className="text-[20px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>
                  Pick an agent to chat with
                </h2>
                <p className="text-[14px] text-[#98a2b3] max-w-[380px] leading-relaxed mx-auto">
                  Or use Group Call / Group Chat from the sidebar for multi-agent conversations.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {agents.map(a => {
                  const avatarUrl = avatarSrc(a) ?? null;
                  return (
                    <button
                      key={a.id}
                      onClick={() => handleQuickAgentChat(a)}
                      className="flex items-center gap-3 px-3.5 py-3 rounded-xl border border-gray-100 bg-white hover:border-[#be43ff]/20 hover:bg-[#faf5ff] hover:shadow-sm transition-all text-left"
                    >
                      {avatarUrl ? (
                        <img src={avatarUrl} alt={a.name} className="w-9 h-9 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div
                          className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0"
                          style={{
                            background: `linear-gradient(135deg, ${a.gradientFrom}20, ${a.gradientTo}20)`,
                            border: `1.5px solid ${a.accentColor}25`,
                          }}
                        >
                          {a.avatarEmoji}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-[13px] text-[#101828] truncate" style={{ fontWeight: 600 }}>{a.name}</div>
                        <div className="text-[11px] text-[#98a2b3] truncate">{a.title}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Group picker modals ── */}
        {showGroupCallPicker && (
          <CallInvitePanel
            agents={agents}
            initialSelected={[]}
            onStart={handleStartGroupCall}
            onClose={() => setShowGroupCallPicker(false)}
            title="Start Group Call"
            actionLabel="Start Call"
          />
        )}
        {showGroupChatPicker && (
          <CallInvitePanel
            agents={agents}
            initialSelected={[]}
            onStart={handleStartGroupChat}
            onClose={() => setShowGroupChatPicker(false)}
            title="Start Group Chat"
            actionLabel="Start Chat"
            actionIcon="chat"
          />
        )}
      </div>
    </div>
  );
}
